import { FinancialUnavailable, isCurrentAssessment, type FinancialResponseAvailability } from '../financial-unavailable';
/**
 * Serviceability Dashboard Panel (SPRINT-6B F012)
 *
 * Unified dashboard integrating all serviceability panels with:
 * - Summary KPIs (ICR, DSCR, DSR, Surplus Income, Health Score)
 * - Overall status badge (Pass/Marginal/Fail)
 * - Recommendation badge (Approve/Conditional Approve/Refer/Decline)
 * - Financial summary section
 * - Panel section tabs/accordions
 * - Recalculate button
 * - Warnings display
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, TrendingUp, TrendingDown, AlertTriangle, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { DealServiceabilityPanel } from './deal-serviceability-panel';
import { IcrAnalysisPanel } from './icr-analysis-panel';
import { DsrAnalysisPanel } from './dsr-analysis-panel';
import { IncomeSourcesPanel } from './income-sources-panel';
import { ContactIncomePanel } from './contact-income-panel';
import { DealAdjustmentsPanel } from './deal-adjustments-panel';
import { LenderHealthScoresPanel } from './lender-health-scores-panel';

interface ServiceabilitySummary extends FinancialResponseAvailability {
  dealId: string;
  dealName: string;
  assessmentDate: string;
  overallStatus: 'pass' | 'marginal' | 'fail' | 'unassessed';
  kpis: {
    icr: {
      value: number | null;
      status: 'strong' | 'adequate' | 'marginal' | 'weak' | null;
      trend: 'improving' | 'stable' | 'declining' | null;
    };
    dscr: {
      value: number | null;
      status: 'strong' | 'adequate' | 'marginal' | 'weak' | null;
      trend: 'improving' | 'stable' | 'declining' | null;
    };
    dsr: {
      value: number | null;
      status: 'strong' | 'adequate' | 'marginal' | 'weak' | null;
      trend: 'improving' | 'stable' | 'declining' | null;
    };
    surplusIncome: {
      value: number | null;
      status: 'positive' | 'negative' | null;
    };
    healthScore: {
      value: number | null;
      rating: string | null;
    };
  };
  summary: {
    totalIncome: number | null;
    totalCommitments: number | null;
    proposedDebtService: number | null;
    netSurplus: number | null;
  };
  warnings: Array<{ type: string; message: string }>;
  recommendation: 'approve' | 'conditional_approve' | 'refer' | 'decline' | null;
}

interface ServiceabilityDashboardPanelProps {
  dealId: string;
}

export function ServiceabilityDashboardPanel({ dealId }: ServiceabilityDashboardPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch serviceability summary (uses default queryFn with auth)
  const { data: summary, isLoading, isError } = useQuery<ServiceabilitySummary>({
    queryKey: ['/api/crm/deals', dealId, 'serviceability-summary'],
  });

  // Recalculate mutation
  const recalculateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/crm/deals/${dealId}/serviceability-summary/recalculate`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/crm/deals', dealId, 'serviceability-summary'] });
      toast({
        title: 'Recalculation complete',
        description: 'Serviceability metrics have been recalculated.',
      });
    },
    onError: () => {
      toast({
        title: 'Recalculation failed',
        description: 'Unable to recalculate serviceability metrics.',
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (value: number | null) => {
    if (value == null) return 'Not assessed';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'pass':
      case 'strong':
      case 'positive':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'marginal':
      case 'adequate':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'fail':
      case 'weak':
      case 'negative':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getRecommendationColor = (rec: string | null) => {
    switch (rec) {
      case 'approve':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'conditional_approve':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'refer':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'decline':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatRecommendation = (rec: string | null) => {
    switch (rec) {
      case 'approve':
        return 'Approve';
      case 'conditional_approve':
        return 'Conditional Approve';
      case 'refer':
        return 'Refer';
      case 'decline':
        return 'Decline';
      default:
        return rec ?? 'Not assessed';
    }
  };

  const formatStatus = (status: string | null) => {
    return status == null ? 'Not assessed' : status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div data-testid="serviceability-dashboard" className="space-y-4">
      {/* Assessment availability must not hide the inputs needed to repair it. */}
      {isError || recalculateMutation.isError || !summary || !isCurrentAssessment(summary) ? (
        <Card>
          <CardHeader><CardTitle>Serviceability</CardTitle></CardHeader>
          <CardContent>
            <FinancialUnavailable dealId={dealId} value={isError || recalculateMutation.isError ? { assessmentStatus: 'failed', historical: summary } : summary} action="Review financial inputs, then recalculate." />
            <Button onClick={() => recalculateMutation.mutate()} disabled={recalculateMutation.isPending}>Recalculate</Button>
          </CardContent>
        </Card>
      ) : (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Deal Serviceability Dashboard</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {summary.dealName}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Assessment Date: {new Date(summary.assessmentDate).toLocaleDateString('en-AU')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge data-testid="overall-status-badge" className={getStatusColor(summary.overallStatus)}>
                {formatStatus(summary.overallStatus)}
              </Badge>
              <Badge data-testid="recommendation-badge" className={getRecommendationColor(summary.recommendation)}>
                {formatRecommendation(summary.recommendation)}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => recalculateMutation.mutate()}
                disabled={recalculateMutation.isPending}
              >
                {recalculateMutation.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span className="ml-2">Recalculate</span>
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* KPI Cards */}
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* ICR KPI */}
            <Card data-testid="kpi-card-icr">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-muted-foreground">ICR</p>
                  <Badge variant="outline" className={getStatusColor(summary.kpis.icr.status)}>
                    {summary.kpis.icr.status}
                  </Badge>
                </div>
                <p className="text-2xl font-bold">{(summary.kpis.icr.value == null ? 'Not assessed' : Number(summary.kpis.icr.value).toFixed(2))}</p>
                <div className="flex items-center text-xs text-muted-foreground mt-1">
                  {summary.kpis.icr.trend === 'improving' && <TrendingUp className="h-3 w-3 mr-1 text-green-600" />}
                  {summary.kpis.icr.trend === 'declining' && <TrendingDown className="h-3 w-3 mr-1 text-red-600" />}
                  {summary.kpis.icr.trend}
                </div>
              </CardContent>
            </Card>

            {/* DSCR KPI */}
            <Card data-testid="kpi-card-dscr">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-muted-foreground">DSCR</p>
                  <Badge variant="outline" className={getStatusColor(summary.kpis.dscr.status)}>
                    {summary.kpis.dscr.status}
                  </Badge>
                </div>
                <p className="text-2xl font-bold">{(summary.kpis.dscr.value == null ? 'Not assessed' : Number(summary.kpis.dscr.value).toFixed(2))}</p>
                <div className="flex items-center text-xs text-muted-foreground mt-1">
                  {summary.kpis.dscr.trend === 'improving' && <TrendingUp className="h-3 w-3 mr-1 text-green-600" />}
                  {summary.kpis.dscr.trend === 'declining' && <TrendingDown className="h-3 w-3 mr-1 text-red-600" />}
                  {summary.kpis.dscr.trend}
                </div>
              </CardContent>
            </Card>

            {/* DSR KPI */}
            <Card data-testid="kpi-card-dsr">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-muted-foreground">DSR</p>
                  <Badge variant="outline" className={getStatusColor(summary.kpis.dsr.status)}>
                    {summary.kpis.dsr.status}
                  </Badge>
                </div>
                <p className="text-2xl font-bold">{(summary.kpis.dsr.value == null ? 'Not assessed' : Number(summary.kpis.dsr.value).toFixed(1))}%</p>
              </CardContent>
            </Card>

            {/* Surplus Income KPI */}
            <Card data-testid="kpi-card-surplus">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-muted-foreground">Surplus Income</p>
                  <Badge variant="outline" className={getStatusColor(summary.kpis.surplusIncome.status)}>
                    {summary.kpis.surplusIncome.status}
                  </Badge>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(summary.kpis.surplusIncome.value)}</p>
              </CardContent>
            </Card>

            {/* Health Score KPI */}
            <Card data-testid="kpi-card-health-score">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-muted-foreground">Health Score</p>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold">{summary.kpis.healthScore.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{summary.kpis.healthScore.rating}</p>
              </CardContent>
            </Card>
          </div>

          {/* Financial Summary Section */}
          <div data-testid="serviceability-summary" className="mt-6">
            <h4 className="text-sm font-semibold mb-3">Financial Summary</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-muted-foreground">Total Available Income</span>
                <span className="text-sm font-medium">{formatCurrency(summary.summary.totalIncome)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-muted-foreground">Total Existing Commitments</span>
                <span className="text-sm font-medium">{formatCurrency(summary.summary.totalCommitments)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-muted-foreground">Proposed Debt Service</span>
                <span className="text-sm font-medium">{formatCurrency(summary.summary.proposedDebtService)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm font-medium">Net Surplus</span>
                <span className="text-sm font-bold text-foreground">
                  {formatCurrency(summary.summary.netSurplus)}
                </span>
              </div>
            </div>
          </div>

          {/* Warnings Section */}
          {summary.warnings.length > 0 && (
            <div data-testid="warnings-section" className="mt-6">
              <h4 className="text-sm font-semibold mb-3 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-2 text-amber-600" />
                Warnings
              </h4>
              <div className="space-y-2">
                {summary.warnings.map((warning, index) => (
                  <Card key={index} data-testid={`warning-card-${index}`} className="bg-amber-50 dark:bg-amber-950/20">
                    <CardContent className="pt-3 pb-3">
                      <p className="text-sm text-amber-800 dark:text-amber-200">{warning.message}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Panel Sections Tabs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Detailed Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="configuration" className="w-full">
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="configuration" data-testid="panel-section-configuration">Configuration</TabsTrigger>
              <TabsTrigger value="icr" data-testid="panel-section-icr">ICR Analysis</TabsTrigger>
              <TabsTrigger value="dsr" data-testid="panel-section-dsr">DSR Analysis</TabsTrigger>
              <TabsTrigger value="income" data-testid="panel-section-income">Income Sources</TabsTrigger>
              <TabsTrigger value="commitments" data-testid="panel-section-commitments">Account Commitments</TabsTrigger>
              <TabsTrigger value="adjustments" data-testid="panel-section-adjustments">Deal Adjustments</TabsTrigger>
              <TabsTrigger value="lender" data-testid="panel-section-lender">Lender Assessment</TabsTrigger>
            </TabsList>

            <TabsContent value="configuration">
              <DealServiceabilityPanel dealId={dealId} />
            </TabsContent>

            <TabsContent value="icr">
              <IcrAnalysisPanel dealId={dealId} />
            </TabsContent>

            <TabsContent value="dsr">
              <DsrAnalysisPanel dealId={dealId} />
            </TabsContent>

            <TabsContent value="income">
              <IncomeSourcesPanel dealId={dealId} />
            </TabsContent>

            <TabsContent value="commitments">
              <ContactIncomePanel dealId={dealId} />
            </TabsContent>

            <TabsContent value="adjustments">
              <DealAdjustmentsPanel dealId={dealId} />
            </TabsContent>

            <TabsContent value="lender">
              <LenderHealthScoresPanel dealId={dealId} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
